import { useFullRelease } from "@/common/hooks_useFullRelease";
import GraphView from "@/components/GraphView/GraphView";
import DecisionFlowNode from "@/components/flow/DecisionFlowNode";
import {
  type FlowStep,
  type FlowTrigger,
  enrichFlowGraphWithMappingNodes,
  generateFlowGraph,
} from "@/components/flow/DecisionFlowUtils";
import type { RequiredDecisiveValue } from "@/components/select/SelectDecisive";
import { useParsedGraph } from "@/pages/models/release/GraphContext";
import type { graphlib } from "@dagrejs/dagre";
import styled from "@emotion/styled";
import React, { useEffect, useImperativeHandle, useState, useMemo } from "react";

const Container = styled.div`
  height: 100%;
  width: 100%;
  //border-top: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  position: relative;
`;

export interface DecisionFlowHandle {
  setRequiredDecisive?: (step: FlowStep, trigger: FlowTrigger, value: RequiredDecisiveValue) => void;
  flowGraph: graphlib.Graph;
  optimisticFlowGraphUpdate: (update: graphlib.Graph) => void;
}

export const DecisionFlowGraphContext = React.createContext<DecisionFlowHandle>(undefined as any);

export interface DecisionFlowGraphProps {
  sectionId?: string;
  rootNodeId?: string;
  style?: React.CSSProperties;
  noDataStyle?: React.CSSProperties;
  onRequiredDecisiveChange?: (step: FlowStep, trigger: FlowTrigger, value: RequiredDecisiveValue) => void;
  ref?: React.Ref<any>;
}

const DecisionFlowGraph = React.memo((props: DecisionFlowGraphProps) => {
  const { ref } = props;
  const { sectionId, rootNodeId, onRequiredDecisiveChange, noDataStyle, style, ...otherProps } = props;

  const graph = useParsedGraph();
  const release = useFullRelease();

  // Memoize the flow graph to prevent unnecessary re-renders; enrich with mapping nodes and mapping→attribute edges when release is available
  const flowGraph = useMemo(() => {
    const base = generateFlowGraph(graph, sectionId, rootNodeId);
    return release ? enrichFlowGraphWithMappingNodes(base, release, graph) : base;
  }, [graph, sectionId, rootNodeId, release]);
  const [optimisticFlowGraph, setOptimisticFlowGraph] = useState<graphlib.Graph | undefined>(undefined);

  useEffect(() => {
    setOptimisticFlowGraph(undefined);
  }, [graph]);

  const currentFlowGraph = useMemo(() => {
    return optimisticFlowGraph ?? flowGraph;
  }, [optimisticFlowGraph, flowGraph]);

  const handle = useMemo(() => {
    return {
      setRequiredDecisive: onRequiredDecisiveChange,
      flowGraph: currentFlowGraph,
      optimisticFlowGraphUpdate: (update: graphlib.Graph) => {
        setOptimisticFlowGraph(update);
      },
    } satisfies DecisionFlowHandle;
  }, [currentFlowGraph, onRequiredDecisiveChange]);

  useImperativeHandle(ref, () => handle, [handle]);

  const hasNodes = currentFlowGraph.nodes().length > 0;
  const isLoading = Boolean(!graph || !currentFlowGraph);

  const flowGraphPropsMemo = useMemo(
    () => ({
      defaultEdgeOptions: {
        type: "smoothstep",
        animated: false,
      },
    }),
    [],
  );

  return (
    <Container
      style={{
        ...style,
        ...(!hasNodes && !isLoading ? noDataStyle : {}),
      }}
      {...otherProps}
    >
      <DecisionFlowGraphContext.Provider value={handle}>
        {hasNodes || isLoading ? (
          <GraphView
            rankDir={"LR"}
            //refitKey={graph.id}
            nodeHeight={600}
            graph={currentFlowGraph}
            loading={isLoading}
            nodeComponent={DecisionFlowNode}
            flowGraphProps={flowGraphPropsMemo}
          >
            {/* <GraphPallet /> TODO ds come back to this - trying to avoid needing to wrap this in a CP..rather need to decouple the CP from the GraphPallet */}
          </GraphView>
        ) : null}
      </DecisionFlowGraphContext.Provider>
      {!hasNodes && !isLoading ? (
        <div className="absolute inset-0 z-[1] flex items-center justify-center">No flows to display</div>
      ) : null}
    </Container>
  );
});

export default DecisionFlowGraph;
